import {useRef, useState} from 'react';
import {logger} from '@/helpers/logger';
import {createRack} from '@/services/rackCatalog';
import {useAppDispatch} from '@/store';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {useRackModalBase} from './useRackModalBase';

export const useAddRackModal = ({onSuccess} = {}) => {
  const dispatch = useAppDispatch();
  const [isPublishing, setIsPublishing] = useState(false);
  const publishInProgress = useRef(false);

  const base = useRackModalBase({actionInProgress: publishInProgress});

  const {
    images,
    rackInformation,
    uploadError,
    isUploading,
    imageLimitReached,
    setPublishError
  } = base;

  const showToast = (message, type = 'info') => {
    dispatch(setToastNotifications({show: true, message, type}));
  };

  const uploadDisabled = isUploading || isPublishing || imageLimitReached;
  const hasChanges =
    images.length > 0 ||
    Object.values(rackInformation).some((value) => value.trim());
  const hasRequiredFields =
    Number.isFinite(Number(rackInformation.points)) &&
    ['displayName', 'rackId', 'points'].every(
      (name) => String(rackInformation[name] ?? '').trim().length > 0
    );
  const canPublish =
    !isUploading &&
    !isPublishing &&
    !uploadError &&
    images.length > 0 &&
    hasRequiredFields;

  const handlePublish = async () => {
    if (publishInProgress.current) return;
    if (!hasRequiredFields) {
      setPublishError(
        'Fill in Rack Display Name, Rack ID and valid Points for the Rack before publishing.'
      );
      return;
    }
    if (images.length === 0) {
      setPublishError('Please select at least one image.');
      return;
    }
    if (!canPublish) return;
    publishInProgress.current = true;
    setIsPublishing(true);
    setPublishError('');
    let response;
    try {
      response = await createRack({
        data: {
          rackId: rackInformation.rackId.trim(),
          displayName: rackInformation.displayName.trim(),
          imageUrls: images.map((image) => image.imageUrl),
          description: rackInformation.description?.trim() || '',
          materialCode: rackInformation.materialCode?.trim() || '',
          category: rackInformation.category?.trim() || '',
          capacity: rackInformation.capacity?.trim() || '',
          width: rackInformation.width?.trim() || '',
          depth: rackInformation.depth?.trim() || '',
          height: rackInformation.height?.trim() || '',
          pointsForRack: Number(rackInformation.points)
        }
      });
      if (response?.success) {
        showToast('Rack published successfully.', 'success');
      } else {
        throw new Error(
          response?.errorMessage ||
            response?.message ||
            'Rack could not be published.'
        );
      }
    } catch (error) {
      logger.error('Rack publish failed:', error);
      const errMsg =
        error?.response?.data?.errorMessage ||
        error?.response?.data?.message ||
        error?.message ||
        'Rack could not be published. Please try again.';
      setPublishError(errMsg);
      showToast(errMsg, 'error');
      return;
    } finally {
      publishInProgress.current = false;
      setIsPublishing(false);
    }
    onSuccess?.(response);
  };

  return {
    ...base,
    isPublishing,
    uploadDisabled,
    hasChanges,
    canPublish,
    handlePublish,
    handleUploadClick: () => base.handleUploadClick(uploadDisabled)
  };
};
